import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip, TextField, InputAdornment, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Search as SearchIcon, SupportAgent as SupportAgentIcon, CheckCircle as CheckCircleIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import ticketService from '../../../services/ticketService';
import useAuth from '../../../hooks/useAuth';
import unifiedSocket from '../../../services/unifiedSocket';

const StatusChip = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const openSet = new Set(['new', 'triage', 'queued', 'assigned', 'reopened', 'open']);
  const pendingSet = new Set(['pending_customer', 'pending_internal', 'pending']);
  const closedSet = new Set(['resolved', 'closed']);
  const color = openSet.has(s) ? 'success' : pendingSet.has(s) ? 'warning' : closedSet.has(s) ? 'default' : 'default';
  const label = openSet.has(s) ? 'Em Aberto' : pendingSet.has(s) ? 'Pendente' : closedSet.has(s) ? 'Finalizado' : s || '—';
  return <Chip label={label} color={color} size="small" />;
};

const SlaChip = ({ sla }) => {
  const rs = String(sla?.resolutionStatus || '').toLowerCase();
  const fr = String(sla?.firstResponseStatus || '').toLowerCase();
  const status = rs || fr;
  if (!status) return <Chip label="—" size="small" variant="outlined" />;
  const label = status === 'overdue' ? 'Estourado' : status === 'near_due' ? 'Perto' : 'Dentro';
  const color = status === 'overdue' ? 'error' : status === 'near_due' ? 'warning' : 'success';
  return <Chip label={label} color={color} size="small" />;
};

const TicketsPage = () => {
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const socketCompanyId = currentUser?.companyId || 1;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [queues, setQueues] = useState([{ id: 'all', name: 'Todas as Filas', color: '#666' }]);
  const [selectedQueue, setSelectedQueue] = useState('all');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTicket, setAssignTicket] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [autoRefreshStarted, setAutoRefreshStarted] = useState(false);

  const viewOpenRef = useRef(false);
  const viewTicketRef = useRef(null);
  useEffect(() => {
    viewOpenRef.current = !!viewOpen;
    viewTicketRef.current = viewTicket || null;
  }, [viewOpen, viewTicket]);

  const fetchQueues = useCallback(async () => {
    const list = await ticketService.getQueues();
    const normalized = Array.isArray(list) ? list : [];
    setQueues([{ id: 'all', name: 'Todas as Filas', color: '#666' }, ...normalized]);
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== 'all') params.status = status;
      if (selectedQueue && selectedQueue !== 'all') params.queueId = selectedQueue;
      if (search) params.searchParam = search;
      const resp = await ticketService.getTickets(params);
      setTickets(resp?.tickets || []);
    } catch (_) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [status, selectedQueue, search]);

  useEffect(() => { fetchQueues(); }, [fetchQueues]);
  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => {
    if (autoRefreshStarted) return;
    const interval = setInterval(() => { loadTickets(); }, 30000);
    setAutoRefreshStarted(true);
    return () => clearInterval(interval);
  }, [autoRefreshStarted, loadTickets]);
  useEffect(() => {
    if (!socketUserId || !token) return;

    const companyId = socketCompanyId;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;

    const onTicketEvent = (data) => {
      const updatedTicket = data?.ticket || data;
      if (!updatedTicket || !updatedTicket.id) return;
      setTickets(prev => {
        const exists = prev.find(t => t.id === updatedTicket.id);
        if (exists) return prev.map(t => t.id === updatedTicket.id ? { ...t, ...updatedTicket } : t);
        return [updatedTicket, ...prev];
      });
    };

    const onMessageEvent = (data) => {
      const message = data?.message || data;
      const ticketId = message?.ticketId || data?.ticket?.id;
      const opened = viewOpenRef.current;
      const openedTicket = viewTicketRef.current;
      if (opened && openedTicket && ticketId === openedTicket.id) {
        const normalized = { ...message, timestamp: message?.timestamp || message?.createdAt || Date.now() };
        setMessages(prev => {
          const exists = prev.some(m => (
            (m.id && normalized.id && m.id === normalized.id) ||
            (m.messageId && normalized.messageId && m.messageId === normalized.messageId)
          ));
          if (exists) {
            return prev.map(m => (
              (m.id && normalized.id && m.id === normalized.id) ||
              (m.messageId && normalized.messageId && m.messageId === normalized.messageId)
                ? { ...m, ...normalized }
                : m
            ));
          }
          return [...prev, normalized];
        });
      }
    };

    socket.on(`company-${companyId}-ticket`, onTicketEvent);
    socket.on(`company-${companyId}-appMessage`, onMessageEvent);
    socket.on('message.created', (payload) => onMessageEvent({ action: 'create', message: payload?.message, ticket: payload?.ticket }));
    socket.on('ticket.updated', (payload) => onTicketEvent({ action: 'update', ticket: payload?.ticket }));

    return () => {
      try { socket.off(`company-${companyId}-ticket`, onTicketEvent); } catch {}
      try { socket.off(`company-${companyId}-appMessage`, onMessageEvent); } catch {}
    };
  }, [socketUserId, socketCompanyId, token]);

  const openAssign = (ticket) => {
    setAssignTicket(ticket);
    setAssignOpen(true);
  };

  const doAssign = async () => {
    if (!assignTicket) return;
    try {
      await ticketService.acceptTicket(assignTicket.id);
      setAssignOpen(false);
      setAssignTicket(null);
      await loadTickets();
    } catch (_) {}
  };

  const openView = async (ticket) => {
    setViewTicket(ticket);
    setViewOpen(true);
    try {
      const msgs = await ticketService.getTicketMessages(ticket.id);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch {
      setMessages([]);
    }
  };

  const filtered = tickets;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Tickets</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Fila</InputLabel>
            <Select value={selectedQueue} label="Fila" onChange={(e) => setSelectedQueue(e.target.value)}>
              {queues.map(q => (
                <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="open">Em Atendimento</MenuItem>
              <MenuItem value="pending">Pendente</MenuItem>
              <MenuItem value="closed">Finalizado</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} sx={{ minWidth: 280 }} />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTickets}>Atualizar</Button>
        </Box>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Protocolo</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Fila</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>SLA</TableCell>
              <TableCell>Operador</TableCell>
              <TableCell>Atualizado</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8}><Box p={3}><Typography>Carregando...</Typography></Box></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8}><Box p={3}><Typography>Nenhum ticket</Typography></Box></TableCell></TableRow>
            ) : (
              filtered.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.protocol}</TableCell>
                  <TableCell>{t.contact?.name || t.contact?.number}</TableCell>
                  <TableCell>{t.queue?.name}</TableCell>
                  <TableCell><StatusChip status={t.status} /></TableCell>
                  <TableCell><SlaChip sla={t.sla} /></TableCell>
                  <TableCell>{t.user?.name || '-'}</TableCell>
                  <TableCell>{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">
                    {!t.userId && (
                      <Tooltip title="Atribuir ao meu usuário">
                        <Button variant="contained" startIcon={<SupportAgentIcon />} onClick={() => openAssign(t)}>
                          Atribuir
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Ver conversa completa">
                      <Button sx={{ ml: 1 }} variant="outlined" onClick={() => openView(t)}>
                        Ver
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SupportAgentIcon color="primary" />
            Atribuir Ticket
          </Box>
        </DialogTitle>
        <DialogContent>
          {assignTicket && (
            <Box>
              <Typography variant="body1">Deseja atribuir este ticket ao seu usuário?</Typography>
              <Box mt={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2"><strong>Contato:</strong> {assignTicket.contact?.number}</Typography>
                <Typography variant="subtitle2"><strong>Fila:</strong> {assignTicket.queue?.name}</Typography>
                <Typography variant="subtitle2"><strong>Status:</strong> {assignTicket.status}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancelar</Button>
          <Button onClick={doAssign} variant="contained" startIcon={<CheckCircleIcon />}>Atribuir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {viewTicket ? (
            <Box>
              <Typography variant="h6">Conversas do Ticket</Typography>
              <Typography variant="body2">Protocolo: {viewTicket.protocol || '-'}</Typography>
              <Typography variant="body2">Contato: {viewTicket.contact?.name || viewTicket.contact?.number}</Typography>
              <Typography variant="body2">Fila: {viewTicket.queue?.name || '-'}</Typography>
              <Typography variant="body2">Status: {viewTicket.status}</Typography>
            </Box>
          ) : 'Conversas'}
        </DialogTitle>
        <DialogContent dividers>
          {messages.length === 0 ? (
            <Box p={2}><Typography>Nenhuma mensagem</Typography></Box>
          ) : (
            <Box>
              {messages.map((m) => (
                <Box key={m.id || m.messageId || Math.random()} mb={1} display="flex" justifyContent={m.fromMe ? 'flex-end' : 'flex-start'}>
                  <Box sx={{ maxWidth: '70%', backgroundColor: m.fromMe ? 'primary.main' : '#f5f5f5', color: m.fromMe ? 'white' : 'inherit', borderRadius: 2, p: 1.5 }}>
                    <Typography variant="body2">{m.body}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {new Date(m.timestamp || m.createdAt || Date.now()).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Fechar</Button>
          {viewTicket && (
            <Button variant="outlined" onClick={() => openView(viewTicket)}>Atualizar</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketsPage;
